import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { ClaimKey } from '../../../models/api/core/claim-key';
import { ExportService } from '../../../services/export/export.service';
import { RecentClaimsService } from '../../../services/recent-claims/recent-claims.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { WatchlistService } from '../../../services/view/watchlist.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { ClaimViewComponent } from '../claim-view/claim-view.component';

@Component({
  selector: 'app-recent-claims',
  imports: [
    CommonModule,
    LoadingPanelComponent,
    RouterModule,
    ClaimViewComponent,
    EmptyStateComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="state"
      [error]="error"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
    >
      <ng-template #empty>
        <app-empty-state
          icon="fa-light fa-history"
          label="Empty history"
          height="450px"
        >
          <span
            >Previously viewed claims will appear here. Try
            <app-link-label (click)="searchClick.emit()"
              >searching</app-link-label
            >

            for a claim.</span
          >
        </app-empty-state>
      </ng-template>

      <ng-template #loaded>
        <app-claim-view
          [claimView]="recentClaims.claims()"
          [columnView]="recentClaims.claims()"
          [showSelection]="true"
          [showRemove]="true"
          [showDateAccessed]="true"
          [claimViewAriaLabel]="'History'"
          [persistPaginationSettings]="true"
          sortField="dateAccessed"
          [sortOrder]="-1"
          (removeRows)="onRemoveRows($event)"
          (export)="onExport($event)"
          (columnsUpdated)="onColumnsUpdated()"
        />
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class RecentClaimsComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  recentClaims = inject(RecentClaimsService);
  exportService = inject(ExportService);
  private watchlist = inject(WatchlistService);
  private datePipe = inject(DatePipe);

  @Output()
  searchClick = new EventEmitter();

  protected state: LoadingState = 'Loading';

  error: any = null;

  constructor() {
    super();
    this.dispose.subs = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async (event: NavigationEnd) => {
        // this route is cached so ngOnInit only fires once.
        // We need to reload recent claims to pick up changes.
        if (event.url === '/view') {
          await this.loadClaims();
        }
      });
  }

  async ngOnInit() {
    await this.loadClaims();
  }

  async loadClaims() {
    this.error = null;
    this.state = 'Loading';
    try {
      await this.recentClaims.load(true);
      const data = this.recentClaims.claims();

      if (data) {
        this.state = data.rows.length > 0 ? 'Loaded' : 'Empty';
      } else {
        this.state = 'Empty';
      }
    } catch (err) {
      this.error = err;
      this.state = 'Empty';
    }
  }

  async onColumnsUpdated() {
    try {
      this.state = 'Loading';
      await this.recentClaims.forceLoad();
      const data = this.recentClaims.claims();
      this.state = data?.rows.length ? 'Loaded' : 'Empty';
    } catch (err) {
      this.error = err;
      this.state = 'Empty';
    }
  }

  async onRemoveRows(event: { type: 'selected' | 'all'; rows?: any[] }) {
    this.error = null;

    try {
      if (event.type === 'selected') {
        const claimKeys: ClaimKey[] = event.rows.map((r) => ({
          recordId: r.recordId,
          recordSource: r.recordSource,
        }));

        if (claimKeys.length === 0) {
          this.snackbar.show(
            'Please select at least one claim to remove.',
            'Error',
          );
          return;
        }
        this.state = 'Loading';
        await this.recentClaims.remove(claimKeys);
        this.snackbar.show('Selected claim(s) successfully removed', 'Success');
      } else if (event.type === 'all') {
        this.state = 'Loading';
        await this.recentClaims.clear();
        this.snackbar.show('Claims successfully removed', 'Success');
      }

      const claims = this.recentClaims.claims();
      this.state = claims.rows.length ? 'Loaded' : 'Empty';
    } catch (err) {
      this.error = err;
      this.state = 'Empty';
    }
  }

  async onExport(format: ExportFormat) {
    try {
      const claimRows = this.recentClaims.claims().rows;
      const watchedClaims = this.watchlist.claims()?.rows ?? [];
      const rows = claimRows.map((row) => ({
        ...row,
        watchStatus: watchedClaims.some(
          (w) =>
            w.recordId === row.recordId && w.recordSource === row.recordSource,
        )
          ? 'Y'
          : 'N',
      }));
      const claimColumns = this.recentClaims.claims().columns;
      const columns = [
        {
          propertyName: 'watchStatus',
          headerText: 'Watchlist',
        },
        ...claimColumns,
      ];
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `History_${formattedDate}.${format}`;

      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'History');
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'History',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (error) {
      this.error = error;
    }
  }
}
